/**
 * New Frontend System plugin export for tenant-management.
 *
 * This module provides the tenant-management plugin using the new Backstage
 * frontend system architecture with extensions and blueprints.
 *
 * @packageDocumentation
 */

import type { JSX } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  ApiBlueprint,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import BusinessIcon from '@mui/icons-material/Business';
import UpgradeIcon from '@mui/icons-material/Upgrade';

import {
  tenantManagementApiRef,
  TenantManagementApiClient,
} from './api/TenantManagementApi';
import { licensingApiRef, LicensingApiClient } from './api/LicensingApi';
import {
  rootRouteRef,
  tenantDetailsRouteRef,
  upgradesRouteRef,
} from './routes';

// Re-export route refs for external consumers
export { rootRouteRef, tenantDetailsRouteRef, upgradesRouteRef };

/**
 * API extension for tenant management operations.
 * Provides CRUD operations for tenants, products, and workflows.
 */
const tenantManagementApi = ApiBlueprint.make({
  name: 'tenant-management-api',
  params: defineParams =>
    defineParams({
      api: tenantManagementApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new TenantManagementApiClient({ discoveryApi, fetchApi }),
    }),
});

/**
 * API extension for licensing operations.
 * Provides license data and calculations.
 */
const licensingApi = ApiBlueprint.make({
  name: 'licensing-api',
  params: defineParams =>
    defineParams({
      api: licensingApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new LicensingApiClient({ discoveryApi, fetchApi }),
    }),
});

/**
 * Page extension for the tenant list/dashboard page.
 * Shows all tenants with filtering by product and status.
 */
const tenantListPage = PageBlueprint.make({
  name: 'list',
  params: {
    path: '/tenant-management',
    routeRef: rootRouteRef,
    title: 'Tenants',
    icon: <BusinessIcon fontSize="inherit" />,
    loader: async (): Promise<JSX.Element> => {
      const { TenantListPage } = await import('./components/TenantListPage');
      return <TenantListPage />;
    },
  },
});

/**
 * Page extension for individual tenant details.
 * Multi-tab interface with overview, audit logs, and settings.
 */
const tenantDetailsPage = PageBlueprint.make({
  name: 'details',
  params: {
    path: '/tenant-details/:tenantId',
    routeRef: tenantDetailsRouteRef,
    loader: async (): Promise<JSX.Element> => {
      const { TenantDetailsPage } =
        await import('./components/TenantDetailsPage');
      return <TenantDetailsPage />;
    },
  },
});

/**
 * Page extension for release rollout operations.
 * Coordinates pending upgrades, bulk rollout starts, and rollback dispatch.
 */
const upgradesPage = PageBlueprint.make({
  name: 'upgrades',
  params: {
    path: '/upgrades',
    routeRef: upgradesRouteRef,
    title: 'Upgrades',
    icon: <UpgradeIcon fontSize="inherit" />,
    loader: async (): Promise<JSX.Element> => {
      const { UpgradeDashboardPage } =
        await import('./components/UpgradeDashboardPage');
      return <UpgradeDashboardPage />;
    },
  },
});

/**
 * Tenant Management frontend plugin for the new Backstage frontend system.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * import tenantManagementPlugin from '@internal/plugin-tenant-management/alpha';
 *
 * const app = createApp({
 *   features: [
 *     tenantManagementPlugin,
 *     // ... other plugins
 *   ],
 * });
 * ```
 *
 * @alpha
 */
const tenantManagementPlugin = createFrontendPlugin({
  pluginId: 'tenant-management',
  extensions: [
    tenantManagementApi,
    licensingApi,
    tenantListPage,
    tenantDetailsPage,
    upgradesPage,
  ],
  routes: {
    root: rootRouteRef,
    details: tenantDetailsRouteRef,
    upgrades: upgradesRouteRef,
  },
});

/** @alpha */
export default tenantManagementPlugin;
